import { useState } from 'react';
import { IPriceRule } from '../../types/price.types';
import { IPriceBandWithVenues } from '../../types/priceBand.types';

import { TPricingCategoryRow } from './types';
import { UNCATEGORISED_ID } from '../../utils/pricingCategories';
import { deletePriceRule } from '../../api/deletePriceRule/deletePriceRule';
import { PRICE_RULES_QUERY } from '../../hooks/QueryKeys';
import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { useQueryClient } from '@tanstack/react-query';
import { CategoryOverrideRuleSteps } from '../CategoryOverrideRuleModal';

type TEditingState = {
  rule: IPriceRule | null;
  step: CategoryOverrideRuleSteps | null;
};

const DEFAULT_EDITING_STATE: TEditingState = { rule: null, step: null };

type TOverrideModalState = {
  categoryId: string;
  parentId: string;
  parentName: string;
  parentType: 'price_band' | 'extension_instance';
} | null;

export const usePricingCategoryOverrideActions = (
  priceSchemeId: string,
  priceBands: IPriceBandWithVenues[],
  priceRules: IPriceRule[],
) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [overrideModal, setOverrideModal] = useState<TOverrideModalState>(null);
  const [overrideToDelete, setDeleteOverride] = useState<{ price_rule_id: string; name: string } | null>(null);
  const [editOverride, setEditOverride] = useState<TEditingState>(DEFAULT_EDITING_STATE);

  const handleAddOverride = (row: TPricingCategoryRow) => {
    const parentRule = priceRules.find((r) => {
      if (row.parentType === 'price_band') return r.price_band?.id === row.parentId;
      return r.extension_instance?.id === row.parentId;
    });
    const parentName =
      parentRule?.price_band?.name ??
      parentRule?.extension_instance?.name ??
      priceBands.find((b) => b.priceBandId === row.parentId)?.name ??
      '';
    setOverrideModal({
      parentId: row.parentId,
      parentName,
      parentType: row.parentType,
      categoryId: row.category.category_id ?? UNCATEGORISED_ID,
    });
  };

  const handleDelete = async (ruleId: string) => {
    try {
      await deletePriceRule(ruleId, priceSchemeId);
      await queryClient.invalidateQueries({ queryKey: PRICE_RULES_QUERY });
      showSnackbar({ status: SnackbarStatus.SUCCESS, message: 'Pricing category override deleted successfully' });
    } catch {
      showSnackbar({ status: SnackbarStatus.ERROR, message: 'Failed to delete price category override' });
    } finally {
      setDeleteOverride(null);
    }
  };

  const handleDeleteOverride = (row: TPricingCategoryRow | null) => {
    setDeleteOverride(
      row?.overrideRule ? { price_rule_id: row.overrideRule.price_rule_id, name: row.category.name } : null,
    );
  };

  const handleEditOverride = (row: TPricingCategoryRow, step: CategoryOverrideRuleSteps) => {
    setEditOverride({ rule: row.overrideRule, step });
  };

  const resetEditOverride = () => setEditOverride(DEFAULT_EDITING_STATE);

  return {
    overrideModal,
    setOverrideModal,
    handleAddOverride,
    setOverrideToDelete: handleDeleteOverride,
    handleEditOverride,
    resetEditOverride,
    editingOverride: editOverride,
    handleDeleteOverride: handleDelete,
    overrideToDelete,
  };
};
